import { Button, Toolbar } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Sticker, { fetchStickerReportData } from "./sticker";
import { useLocation } from "react-router-dom";
import DownloadPdfLoaderModal from "../../utils/components/downloadPdfLoader";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from "../image/logos.png";
import moment from "moment";

// =====================================================================
// STICKER SIZE — change these two values any time you want the sticker
// box bigger/smaller. They mirror the old CSS grid:
//   gridTemplateColumns: "repeat(2, 330px)"   -> STICKER_WIDTH_PX
//   gridAutoRows: "265px"                     -> STICKER_HEIGHT_PX
// =====================================================================
const STICKER_WIDTH_PX = 330;
const STICKER_HEIGHT_PX = 265;
const GRID_COLUMN_GAP_PX = 20; // was `columnGap: "20px"`
const GRID_ROW_GAP_PX = 1; // was `rowGap: "5px"`

// CSS px -> PDF pt conversion (96dpi screen px -> 72dpi PDF pt)
const PX_TO_PT = 0.75;

const CELL_WIDTH = STICKER_WIDTH_PX * PX_TO_PT;
const CELL_HEIGHT = STICKER_HEIGHT_PX * PX_TO_PT;
const GUTTER_X = GRID_COLUMN_GAP_PX * PX_TO_PT;
const GUTTER_Y = GRID_ROW_GAP_PX * PX_TO_PT;

// ---- outer page frame (matches the old html2pdf border exactly) ----
const FRAME_X = 6;
const FRAME_Y = 5;
const FRAME_WIDTH = 595.28 - FRAME_X - FRAME_X * 0.95;
const FRAME_HEIGHT = 830;
const CONTENT_PADDING = 12;

const COLS = 2;
const ROWS = 4; // 8 stickers per page, same as the old PDF

const totalGridWidth = COLS * CELL_WIDTH + (COLS - 1) * GUTTER_X;
const GRID_START_X = FRAME_X + (FRAME_WIDTH - totalGridWidth) / 2;
const GRID_START_Y = FRAME_Y + CONTENT_PADDING;

// ---- table column widths (col 0 = logo/label column, like the old ~100px td) ----
const COL0_WIDTH = 75 * PX_TO_PT >= 75 ? 75 : 75; // fixed label/logo column
const COL_OTHER_WIDTH = (CELL_WIDTH - 75) / 2;

// ---- fixed header text (identical for every sticker) ----
const TITLE_FONT_SIZE = 11;
const ADDR_FONT_SIZE = 6.8;
const LINE_GAP = 7.2;
const KALYAN_TEXT = "Kalyan: 113, Blossom Commercial Bldg., Raheja Complex, Kalyan (West), Dist. Thane. Maharashtra 421301 8652233801/ 03 & 9821189331";
const NASHIK_TEXT = "Nashik: 02, Shri Sai Apartment, Pakhal Road, Dwarka, Nashik 422011 8652233800/02/05";

// Convert an imported image (webpack/vite asset URL) to a base64 data URL
// so jsPDF can embed it.
const toDataURL = (url) =>
  fetch(url)
    .then((res) => res.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );

// Precompute everything about the (identical, static) header block once,
// so we don't redo text-wrapping math for every single sticker.
const buildHeaderLayout = (doc) => {
  const textWidth = COL_OTHER_WIDTH * 2 - 12;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(ADDR_FONT_SIZE);
  const kalyanLines = doc.splitTextToSize(KALYAN_TEXT, textWidth);
  const nashikLines = doc.splitTextToSize(NASHIK_TEXT, textWidth);

  const headerRowHeight =
    10 /* top padding */ +
    9 /* title line */ +
    kalyanLines.length * LINE_GAP +
    9 /* gap + separator + gap */ +
    nashikLines.length * LINE_GAP +
    8; /* bottom padding */

  return { kalyanLines, nashikLines, headerRowHeight };
};

// Draws a single sticker as ONE autoTable: row 0 is the logo + header text
const drawSticker = (doc, { templateId, inputStates }, x, y, logoDataUrl, headerLayout) => {
  const { kalyanLines, nashikLines, headerRowHeight } = headerLayout;

  const descriptionValue = inputStates["distinctiveNumber"] || inputStates["typeIdentificationNumber"] || "";

  const body = [
    // Row 0: header (logo in col 0, title/address in merged col 1-2)
    [{ content: "" }, { content: "", colSpan: 2 }],
    [
      { content: "Description", styles: { fontStyle: "bold" } },
      { content: descriptionValue?.trim(), colSpan: 2, styles: { fontStyle: "bold" } },
    ],
    [
      { content: "Type of Testing", styles: { fontStyle: "bold" } },
      { content: "Test Date", styles: { fontStyle: "bold" } },
      { content: "Due Date", styles: { fontStyle: "bold" } },
    ],
  ];

  if (templateId == 12) {
    body.push(
      [
        { content: "Ultrasonic Test", styles: { fontStyle: "bold" } },
        { content: inputStates["utrasonicTestDate"] || "", styles: { fontStyle: "bold" } },
        { content: inputStates["utrasonicExaminationDueDate"] || "", styles: { fontStyle: "bold" } },
      ],
      [
        { content: "Internal Test", styles: { fontStyle: "bold" } },
        { content: inputStates["internalTestDate"] || "", styles: { fontStyle: "bold" } },
        { content: inputStates["internalExaminationDueDate"] || "", styles: { fontStyle: "bold" } },
      ],
      [
        { content: "Hydraulic Test", styles: { fontStyle: "bold" } },
        { content: inputStates["hydraulicTestDate"] || "", styles: { fontStyle: "bold" } },
        { content: inputStates["hydraulicExaminationDueDate"] || "", styles: { fontStyle: "bold" } },
      ]
    );
  } else {
    body.push([
      { content: "", styles: { fontStyle: "bold" } },
      {
        content: inputStates["reportDate"]
          ? moment(inputStates["reportDate"]).format("DD.MM.YYYY")
          : "",
        styles: { fontStyle: "bold" },
      },
      {
        content: inputStates["certificateValidity"]
          ? moment(inputStates["certificateValidity"]).format("DD.MM.YYYY")
          : "",
        styles: { fontStyle: "bold" },
      },
    ]);
  }

  autoTable(doc, {
    startY: y,
    margin: { left: x, right: doc.internal.pageSize.getWidth() - (x + CELL_WIDTH) },
    tableWidth: CELL_WIDTH,
    theme: "grid",
    columnStyles: {
      0: { cellWidth: COL0_WIDTH },
      1: { cellWidth: COL_OTHER_WIDTH },
      2: { cellWidth: COL_OTHER_WIDTH },
    },
    styles: {
      fontSize: 7.5,
      cellPadding: 2.5,
      halign: "center",
      valign: "middle",
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.4,
    },
    body,
    tableLineWidth: 0.4,
    tableLineColor: [0, 0, 0],
    didParseCell: (data) => {
      if (data.row.index === 0) {
        data.cell.styles.minCellHeight = headerRowHeight;
      }
    },
    didDrawCell: (data) => {
      // Logo image in row 0 / column 0 — same pattern as the stamp example.
      if (data.row.index === 0 && data.column.index === 0) {
        const { x: cx, y: cy, width: cw, height: ch } = data.cell;
        const imageSize = Math.min(cw, ch) - 10;
        const centerX = cx + (cw - imageSize) / 2;
        const centerY = cy + (ch - imageSize) / 2;
        if (logoDataUrl) {
          try {
            doc.addImage(logoDataUrl, "PNG", centerX, centerY, imageSize, imageSize);
          } catch (e) {
            console.error("Could not embed logo image", e);
          }
        }
      }

      // Header title + address text in row 0 / column 1 (merged 1-2).
      if (data.row.index === 0 && data.column.index === 1) {
        const { x: cx, y: cy, width: cw } = data.cell;
        const centerX = cx + cw / 2;
        let ty = cy + 14;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(TITLE_FONT_SIZE);
        doc.text("Creative Services", centerX, ty, { align: "center" });
        ty += 9;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(ADDR_FONT_SIZE);
        doc.text(kalyanLines, centerX, ty, { align: "center" });
        ty += kalyanLines.length * LINE_GAP + 2;

        doc.setLineWidth(0.3);
        doc.line(cx + 6, ty, cx + cw - 6, ty);
        ty += 7;

        doc.text(nashikLines, centerX, ty, { align: "center" });
      }
    },
  });
};

const MultipleStickers = () => {
  const location = useLocation();
  const [ids, setIds] = useState([]);
  const elementRef = useRef(null);
  const [downloadPdfloading, setDownlaodPdfLoading] = React.useState(false);

  const generatePDF = async () => {
    setDownlaodPdfLoading(true);
    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
        compress: true,
      });

      const [logoDataUrl, stickersData] = await Promise.all([
        toDataURL(Logo),
        Promise.all(ids.map((id) => fetchStickerReportData(id))),
      ]);

      const headerLayout = buildHeaderLayout(doc);
      const perPage = COLS * ROWS;

      stickersData.forEach((data, index) => {
        const indexOnPage = index % perPage;
        if (index > 0 && indexOnPage === 0) {
          doc.addPage();
        }

        const col = indexOnPage % COLS;
        const row = Math.floor(indexOnPage / COLS);
        const x = GRID_START_X + col * (CELL_WIDTH + GUTTER_X);
        const y = GRID_START_Y + row * (CELL_HEIGHT + GUTTER_Y) + 20;

        drawSticker(doc, data, x, y, logoDataUrl, headerLayout);
      });

      // Full-page outer frame, on every page — matches the old PDF exactly.
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setDrawColor(0);
        doc.setLineWidth(0.6);
        doc.rect(FRAME_X, FRAME_Y, FRAME_WIDTH, FRAME_HEIGHT);
      }

      doc.save("stiker.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setDownlaodPdfLoading(false);
    }
  };

  // const handlePrint = () => {
  //   const printContent = document.getElementById("formToPrint");
  //   const originalContents = document.body.innerHTML;

  //   if (printContent) {
  //     document.body.innerHTML = elementRef.current.innerHTML;
  //     window.print();
  //     document.body.innerHTML = originalContents;
  //     window.location.reload();
  //   }
  // };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const idsParam = searchParams.get("ids");
    if (idsParam) {
      const idsArray = idsParam.split(",").map((id) => parseInt(id));
      setIds(idsArray);
    }
  }, [location.search]);

  return (
    <>
      <Toolbar
        style={{
          padding: "0px",
          overflow: "auto",
          display: "flex",
          justifyContent: "end",
        }}
      >
        <Button
          variant="contained"
          size="small"
          sx={{ marginRight: "20px" }}
          onClick={generatePDF}
        >
          Report Print
        </Button>
      </Toolbar>
      <div
        ref={elementRef}
        style={{ width: "95%",  margin: "5px auto" }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(2, ${STICKER_WIDTH_PX}px)`,
            gridAutoRows: `${STICKER_HEIGHT_PX}px`,
            alignItems: "center",
            justifyContent: "center",
            columnGap: `${GRID_COLUMN_GAP_PX}px`,
            rowGap: `${GRID_ROW_GAP_PX}px`,
          }}
        >
          {ids?.map((ele) => {
            return <Sticker selectedFormID={ele} key={ele} />;
          })}
        </div>
        <br />
        <br />
      </div>

      {downloadPdfloading && (
        <DownloadPdfLoaderModal loading={downloadPdfloading} name={"Stiker"} />
      )}
    </>
  );
};

export default MultipleStickers;




// import { Button, Toolbar } from "@mui/material";
// import React, { useEffect, useRef, useState } from "react";
// import Sticker from "./sticker";
// import { useLocation } from "react-router-dom";
// import DownloadPdfLoaderModal from "../../utils/components/downloadPdfLoader";
// import html2pdf from "html2pdf.js";

// const MultipleStickers = () => {
//   const location = useLocation();
//   const [ids, setIds] = useState([]);
//   const elementRef = useRef(null);
//   const [downloadPdfloading, setDownlaodPdfLoading] = React.useState(false);

//   const generatePDF = async () => {
//     setDownlaodPdfLoading(true);
//     try {
//       const element = elementRef.current;
//       let pdf = await html2pdf()
//         .from(element)
//         .set({
//           margin: [8, 1, 10, 1],
//           filename: `masterInOut.pdf`,
//           pagebreak: { after: "section", mode: ["css", "legacy"] },
//           html2canvas: {
//             dpi: 192,
//             scale: 4,
//             letterRendering: true,
//             useCORS: true,
//           },
//           image: { type: "png", quality: 0.4 },
//           jsPDF: {
//             orientation: "portrait",
//             unit: "pt",
//             format: "a4",
//             compress: true,
//           },
//         })
//         .toPdf()
//         .get("pdf");

//       const totalPages = pdf.internal.getNumberOfPages();
//       for (let i = 1; i <= totalPages; i++) {
//         pdf.setPage(i);
//         pdf.setFontSize(9);
//         pdf.setTextColor(0, 0, 0);
//         pdf.setFont("helvetica", "bold");

//         // border add for contnet
//         const borderWidth = 0.6;
//         const leftMargin = 6;
//         const borderX = leftMargin;
//         const borderY = 5;
//         const rightMargin = 595 - leftMargin * 0.95;
//         pdf.setLineWidth(borderWidth);
//         pdf.rect(borderX, borderY, rightMargin - leftMargin, 830);
//       }

//       const blob = pdf.output("blob");
//       const url = URL.createObjectURL(blob);
//       const a = document.createElement("a");
//       a.href = url;
//       a.download = `stiker.pdf`;
//       a.click();

//       setDownlaodPdfLoading(false);
//     } catch (error) {
//       console.error("Error generating PDF:", error);
//       setDownlaodPdfLoading(false);
//     }
//   };

//   // const handlePrint = () => {
//   //   const printContent = document.getElementById("formToPrint");
//   //   const originalContents = document.body.innerHTML;

//   //   if (printContent) {
//   //     document.body.innerHTML = elementRef.current.innerHTML;
//   //     window.print();
//   //     document.body.innerHTML = originalContents;
//   //     window.location.reload();
//   //   }
//   // };

//   useEffect(() => {
//     const searchParams = new URLSearchParams(location.search);
//     const idsParam = searchParams.get("ids");
//     if (idsParam) {
//       const idsArray = idsParam.split(",").map((id) => parseInt(id));
//       setIds(idsArray);
//     }
//   }, [location.search]);

//   return (
//     <>
//       <Toolbar
//         style={{
//           padding: "0px",
//           overflow: "auto",
//           display: "flex",
//           justifyContent: "end",
//         }}
//       >
//         <Button
//           variant="contained"
//           size="small"
//           sx={{ marginRight: "20px" }}
//           onClick={generatePDF}
//         >
//           Report Print
//         </Button>
//       </Toolbar>
//       <div
//         ref={elementRef}
//         style={{ width: "95%",  margin: "5px auto" }}
//       >
//         <div
//           style={{
//             display: "grid",
//             gridTemplateColumns: "repeat(2, 330px)",
//             gridAutoRows: "265px",
//             alignItems: "center",
//             justifyContent: "center",
//             // gap: "20px",
//             columnGap:"20px",
//             rowGap:"5px"
//           }}
//         >
//           {ids?.map((ele) => {
//             return <Sticker selectedFormID={ele} />;
//           })}
//         </div>
//         <br/>
//         <br/>
//       </div>

//       {downloadPdfloading && (
//         <DownloadPdfLoaderModal loading={downloadPdfloading} name={"Stiker"} />
//       )}
//     </>
//   );
// };

// export default MultipleStickers;